// Growth › Analytics and Lead finder: /api/v1/admin/platform/growth/analytics/*
// and /finder/* (API_CONTRACT.md › Growth; backend/src/routes/admin/
// platform-growth-insights.ts). The shapes live here beside their calls.

import { apiFetch } from './client'
import type { ActivityFacts, Bucket, ISODateTime, Lead, LeadType, PreviewGameRow, SiteSignals, VerificationFacts } from './types'

const BASE = '/admin/platform/growth'

// ─── Analytics ──────────────────────────────────────────────────────────────

export const GA_RANGES = [7, 30, 90] as const
export type GaRange = (typeof GA_RANGES)[number]

export interface GaReport {
  days: GaRange
  totals: { visitors: number; sessions: number; page_views: number; conversions: number }
  trend: Array<{ date: string; visitors: number; sessions: number }>
  pages: Array<{ path: string; views: number; visitors: number }>
  sources: Array<{ source: string; sessions: number; visitors: number }>
  countries: Array<{ country: string; visitors: number }>
  devices: Array<{ device: string; visitors: number }>
  conversions: Array<{ event: string; count: number }>
}

export type SiteAnalytics =
  | { configured: false; missing: string[]; problem: string | null; steps: string[] }
  | { configured: true; days: GaRange; report: GaReport | null; fetched_at: ISODateTime | null; stale: boolean; error: string | null }

export interface FunnelAnalytics {
  days: GaRange
  generated_at: ISODateTime
  funnel: {
    signups: number
    trials_started: number
    trials_now: number
    went_live: number
    live_now: number
    inquiries: number
    leads_added: number
  }
  leads_by_status: Record<string, number>
  inquiries_by_status: Record<string, number>
  campaigns: { available: boolean; campaigns: number; sent: number; clicked: number; replied: number; bounced: number; unsubscribed: number }
  league_traffic: { total_views: number; leagues: Array<{ id: number; name: string; subdomain: string | null; views: number; visitors: number }> }
}

export function siteAnalytics(days: GaRange, signal?: AbortSignal): Promise<SiteAnalytics> {
  return apiFetch(`${BASE}/analytics/site`, { query: { days }, signal })
}

export function funnelAnalytics(days: GaRange, signal?: AbortSignal): Promise<FunnelAnalytics> {
  return apiFetch(`${BASE}/analytics/funnel`, { query: { days }, signal })
}

// ─── Lead finder ────────────────────────────────────────────────────────────

export const FINDER_ADAPTERS = ['manual', 'web_search', 'recleague', 'quickscores'] as const
export type FinderAdapter = (typeof FINDER_ADAPTERS)[number]

export const SEARCH_KINDS = ['youth', 'church', 'rec'] as const
export type SearchKind = (typeof SEARCH_KINDS)[number]

export const CANDIDATE_STATUSES = ['new', 'added', 'rejected', 'duplicate'] as const
export type CandidateStatus = (typeof CANDIDATE_STATUSES)[number]

export const CANDIDATE_SORTS = ['-priority', '-confidence', '-created_at', 'name'] as const
export type CandidateSort = (typeof CANDIDATE_SORTS)[number]

export type RunStatus = 'running' | 'done' | 'stopped' | 'failed'

export interface FinderAdapterInfo { key: FinderAdapter; label: string; description: string; available: boolean; missing: string[] }

export interface FinderStatus {
  adapters: FinderAdapterInfo[]
  budget: { used: number; limit: number }
  active_run_id: number | null
  preview_handoff: boolean
  schedule: { enabled: boolean; hours: number | null }
  /** `platforms`: what By platform can search, and the site it searches. */
  search: { kinds: SearchKind[]; states: Record<string, string>; platforms: Array<{ id: number; name: string; site: string }> }
  limits: { max_urls: number; default_limit: number; max_limit: number }
  rules: { user_agent: string; min_seconds_per_host: number; timeout_seconds: number; max_page_bytes: number; max_redirects: number }
  scoring: { points: Record<string, number>; weak_platforms: string[] }
}

export interface FinderRun {
  id: number
  /** An adapter key, `preview` for a preview-data run, `activity` for an activity check on leads. */
  adapter: string
  trigger: 'manual' | 'schedule'
  params: Record<string, unknown>
  status: RunStatus
  note: string | null
  pages_fetched: number
  pages_refused: number
  candidates_found: number
  duplicates: number
  suppressed: number
  stop_requested: boolean
  /** An activity run's leads, and how many it has dealt with. Otherwise 0. */
  items_total: number
  items_done: number
  started_at: ISODateTime
  updated_at: ISODateTime
  finished_at: ISODateTime | null
}

export interface FinderPageLog {
  id: number
  url: string
  host: string | null
  outcome: string
  http_status: number | null
  detail: string | null
  created_at: ISODateTime
}

export interface RunListResponse { runs: FinderRun[]; total: number; page: number; page_size: number }
export interface RunDetail { run: FinderRun; pages: FinderPageLog[] }

export interface Candidate extends ActivityFacts, VerificationFacts {
  id: number
  name: string
  sport: string | null
  type: LeadType
  city: string | null
  state: string | null
  country: string | null
  website: string | null
  current_platform: string
  platform_id: number | null
  linked_platform: { id: number; name: string } | null
  bucket: Bucket
  site_signals: SiteSignals | null
  email: string | null
  phone: string | null
  source: string
  source_url: string
  /** A pasted Facebook or Instagram link: stored, never fetched. */
  stored_only: boolean
  confidence: number
  /** Why it was suggested. */
  reasons: string[]
  signals: Record<string, unknown>
  priority: number
  priority_reasons: Array<{ key: string; label: string; points: number }>
  status: CandidateStatus
  duplicate_of_lead_id: number | null
  lead_id: number | null
  run_id: number | null
  reviewed_at: ISODateTime | null
  created_at: ISODateTime
  updated_at: ISODateTime
}

export interface CandidateListParams {
  q?: string
  status?: CandidateStatus
  source?: FinderAdapter
  has_email?: boolean
  sort?: CandidateSort
  page?: number
  page_size?: number
}

export interface CandidateListResponse {
  candidates: Candidate[]
  total: number
  page: number
  page_size: number
  status_counts: Record<CandidateStatus | 'all', number>
}

export interface PreviewDraft {
  id: number
  candidate_id: number
  source_url: string
  teams: Array<{ name: string }>
  standings: Array<{ team: string; wins: number | null; losses: number | null; ties: number | null }>
  games: Array<{ date: string | null; time: string | null; home: string; away: string; home_score: number | null; away_score: number | null; venue: string | null }>
  tables_skipped: number
  handed_off_at: ISODateTime | null
  /** Set by a hand-off. `serving` is false when the preview has no live link, so a campaign using {{preview_url}} would skip the lead. */
  handoff: { preview_id: number | null; summary: unknown; games_left_out: number; serving: boolean } | null
  created_at: ISODateTime
  updated_at: ISODateTime
}

export interface CandidateDetail { candidate: Candidate; draft: PreviewDraft | null }

export interface BulkCandidateResult {
  action: 'add' | 'reject'
  added: Array<{ candidate_id: number; lead_id: number; name: string }>
  rejected: number[]
  duplicates: Array<{ candidate_id: number; lead_id: number | null }>
  skipped: Array<{ candidate_id: number; reason: string }>
  missing: number
}

export type StartRunBody =
  | { adapter: 'manual' | 'recleague' | 'quickscores'; urls: string }
  | { adapter: 'web_search'; kinds: SearchKind[]; state: string; sport?: string; limit?: number }
  | { adapter: 'web_search'; mode: 'platform'; platform_id: number; state: string; sport?: string; limit?: number }

const F = `${BASE}/finder`

export function finderStatus(signal?: AbortSignal): Promise<FinderStatus> {
  return apiFetch(`${F}/status`, { signal })
}

export function listRuns(params: { page?: number; page_size?: number } = {}, signal?: AbortSignal): Promise<RunListResponse> {
  return apiFetch(`${F}/runs`, { query: params, signal })
}

export function getRun(id: number, signal?: AbortSignal): Promise<RunDetail> {
  return apiFetch(`${F}/runs/${id}`, { signal })
}

/** 202: the run goes on in the background. 409 finder_busy, 409 adapter_unavailable. */
export async function startRun(body: StartRunBody): Promise<FinderRun> {
  const r = await apiFetch<{ run: FinderRun }>(`${F}/runs`, { method: 'POST', body })
  return r.run
}

export function stopRun(id: number): Promise<RunDetail> {
  return apiFetch(`${F}/runs/${id}/stop`, { method: 'POST' })
}

export function listCandidates(params: CandidateListParams = {}, signal?: AbortSignal): Promise<CandidateListResponse> {
  return apiFetch(`${F}/candidates`, { query: { ...params, q: params.q?.trim() || undefined }, signal })
}

export function getCandidate(id: number, signal?: AbortSignal): Promise<CandidateDetail> {
  return apiFetch(`${F}/candidates/${id}`, { signal })
}

/** 409 duplicate_lead, do_not_contact or candidate_closed. */
export function addCandidate(id: number): Promise<CandidateDetail & { lead: Lead }> {
  return apiFetch(`${F}/candidates/${id}/add`, { method: 'POST' })
}

export function rejectCandidate(id: number): Promise<CandidateDetail> {
  return apiFetch(`${F}/candidates/${id}/reject`, { method: 'POST' })
}

/** 202: reads where each candidate lives today, as a run. 409 finder_busy while another run is going. */
export async function verifyCandidates(ids: number[]): Promise<FinderRun> {
  const r = await apiFetch<{ run: FinderRun }>(`${BASE}/finder/candidates/verify`, { method: 'POST', body: { ids } })
  return r.run
}

export function bulkCandidates(ids: number[], action: 'add' | 'reject'): Promise<BulkCandidateResult> {
  return apiFetch(`${F}/candidates/bulk`, { method: 'POST', body: { ids, action } })
}

/** 202: reads the league page in the background; the draft appears on the candidate. */
export async function startPreviewDraft(id: number): Promise<FinderRun> {
  const r = await apiFetch<{ run: FinderRun }>(`${F}/candidates/${id}/preview-draft`, { method: 'POST' })
  return r.run
}

/** What the last read of a lead's own site found, for Build a preview. */
export interface LeadPreviewDraft {
  /** The addresses a read takes, in order: the lead's listing, then its website. Empty: nothing to read. */
  urls: string[]
  /** The last read, running or finished. */
  run: FinderRun | null
  draft: (Omit<PreviewDraft, 'candidate_id' | 'handed_off_at' | 'handoff'> & { lead_id: number }) | null
  /** The draft as a preview's rows: dates as YYYY-MM-DD, times as HH:MM, each date a round. */
  rows: { teams: Array<{ name: string }>; games: Array<Required<Pick<PreviewGameRow, 'home_team' | 'away_team' | 'game_date'>> & { game_time?: string; status: 'scheduled' | 'final'; home_score: number | null; away_score: number | null; round_number: number }> } | null
  /** Games with no date that could be read, left out of rows. */
  left_out: number
}

export function leadPreviewDraft(leadId: number, signal?: AbortSignal): Promise<LeadPreviewDraft> {
  return apiFetch(`${BASE}/leads/${leadId}/preview-draft`, { signal })
}

/** 202: reads the lead's own site in the background. 409 no_site, finder_busy or do_not_contact. */
export async function readLeadSite(leadId: number): Promise<FinderRun> {
  const r = await apiFetch<{ run: FinderRun }>(`${BASE}/leads/${leadId}/preview-draft`, { method: 'POST' })
  return r.run
}

/** 409 preview_unavailable until the Preview stage is connected. */
export function handOffPreview(id: number): Promise<CandidateDetail> {
  return apiFetch(`${F}/candidates/${id}/preview-handoff`, { method: 'POST' })
}
